import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDownTrayIcon, DocumentIcon, TrashIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { EventAttachment } from '@flock-types/events.types';
import RoleGate from '@/components/layout/RoleGate';
import { useToast } from '@/components/Toast';
import { useAuth } from '@/context/AuthContext';
import { eventsService } from '@/services/events.service';

function fileKind(contentType: string): { label: string; className: string } {
  if (contentType === 'application/pdf') return { label: 'PDF', className: 'bg-red-100 text-red-700' };
  if (contentType.includes('word')) return { label: 'Word', className: 'bg-blue-100 text-blue-700' };
  if (contentType.includes('presentation') || contentType.includes('powerpoint'))
    return { label: 'PPT', className: 'bg-amber-100 text-amber-700' };
  return { label: 'File', className: 'bg-flock-navy-50 text-flock-navy-600' };
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  eventId: string;
  /** Pin requests to the event's org (cross-org calendar). */
  organizationId?: string;
  attachments: EventAttachment[];
}

/** Attachment rows: type badge, name, size; opens in a new tab. Leader delete. */
export default function AttachmentList({ eventId, organizationId, attachments }: Props) {
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const orgId = organizationId ?? currentOrganization?.id;

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: string) =>
      eventsService.deleteAttachment(eventId, attachmentId, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [orgId, 'event-attachments', eventId],
      });
      showToast('Attachment removed');
    },
    onError: () => showToast("Couldn't remove the attachment.", 'error'),
  });

  if (attachments.length === 0) return null;

  return (
    <ul className="space-y-1.5">
      {attachments.map((att) => {
        const kind = fileKind(att.content_type);
        return (
          <li key={att.id} className="group flex items-center gap-2">
            <a
              href={att.download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg border border-flock-gray-200 px-2.5 py-2 transition hover:border-flock-navy-300 hover:bg-flock-navy-50/50"
            >
              <span
                className={clsx(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                  kind.className
                )}
              >
                <DocumentIcon className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-flock-navy-800">
                  {att.original_file_name}
                </span>
                <span className="text-[11px] text-flock-navy-400">
                  {kind.label} · {formatSize(att.file_size)}
                </span>
              </span>
              <ArrowDownTrayIcon className="h-4 w-4 shrink-0 text-flock-navy-300 transition group-hover:text-flock-navy-500" />
            </a>
            <RoleGate minRole="group_leader">
              <button
                onClick={() => {
                  if (window.confirm(`Remove ${att.original_file_name}?`)) {
                    deleteMutation.mutate(att.id);
                  }
                }}
                disabled={deleteMutation.isPending}
                title="Remove attachment"
                className="rounded-lg p-1.5 text-flock-navy-300 transition hover:bg-red-50 hover:text-red-600"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </RoleGate>
          </li>
        );
      })}
    </ul>
  );
}
